/**
 * §P.4 fase 2 · Las devoluciones hechas.
 *
 * ## Existe para cuadrar contra el extracto del banco
 *
 * El contrato lo dice donde define la ruta: «de la más reciente hacia atrás, con el
 * recibo y el pagador al lado: una devolución sin saber a quién se le hizo no se
 * puede cuadrar contra el extracto del banco». Así que las tres columnas que no
 * pueden faltar son **cuánto, a quién y de qué recibo**.
 *
 * ## Aquí no se devuelve nada
 *
 * Se devuelve desde el cobro, en la ficha del recibo, y eso no es un descuido de esta
 * pantalla: una devolución cuelga de **un cobro concreto** —«el Bizum de marzo», no
 * «marzo»—, y un botón aquí obligaría a elegir el cobro sin tener el recibo delante.
 * Esta pantalla es el registro; el botón está donde está el dato que hace falta.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros, fechaYHora } from '@/shared/ui/formato';
import {
  etiquetaDeEstadoDeDevolucion,
  pastillaDeDevolucion,
  type Devolucion,
} from '@/club/facturacion/tipos';

const TOPE = 200;

export function Devoluciones({
  club,
  alAbrirRecibo,
}: {
  club: string;
  alAbrirRecibo: (reciboId: string) => void;
}) {
  const devoluciones = useQuery({
    queryKey: ['devoluciones', club],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .refunds.get({ query: { limit: TOPE } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Devolucion[];
    },
  });

  if (devoluciones.isPending) {
    return <Cargando filas={4} etiqueta="Cargando las devoluciones" />;
  }

  if (devoluciones.isError || !devoluciones.data) {
    return (
      <PanelError
        problema={aProblema(devoluciones.error)}
        reintentar={() => void devoluciones.refetch()}
      />
    );
  }

  const lista = devoluciones.data;
  const total = lista
    .filter((d) => d.status !== 'failed')
    .reduce((s, d) => s + d.amount_cents, 0);

  return (
    <section>
      <h1>Devoluciones</h1>

      <p className="tenue">
        Dinero que el club ha devuelto. Se devuelve desde el cobro, en la ficha
        del recibo: <strong>una devolución es de un cobro concreto</strong>, no
        de un mes.
      </p>

      {lista.length === 0 ? (
        <Vacio
          titulo="No has devuelto nada"
          explicacion="Cuando haya que devolver un cobro —una baja a mitad de mes, un Bizum duplicado— se hace desde el recibo y aparecerá aquí para poder cuadrarlo con el banco."
        />
      ) : (
        <>
          <dl className="datos">
            <div>
              <dt>Devuelto en total</dt>
              {/* Sin las fallidas: de esas no salió dinero. */}
              <dd>{euros(total)}</dd>
            </div>
            <div>
              <dt>Cuántas</dt>
              <dd>{lista.length}</dd>
            </div>
          </dl>

          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Cuándo</th>
                  <th>A quién</th>
                  <th>Recibo</th>
                  <th className="num">Importe</th>
                  <th>Motivo</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((d) => (
                  <tr key={d.id}>
                    <td>{fechaYHora(d.created_at)}</td>
                    <td>{d.payer_name}</td>
                    <td>
                      <button
                        type="button"
                        className="enlace-fila"
                        onClick={() => alAbrirRecibo(d.invoice_id)}
                      >
                        {/*
                          El número si lo tiene; si no, «sin número», que es lo que le
                          pasa a un recibo que se anuló antes de emitirse.
                        */}
                        {d.invoice_number ?? 'Sin número'}
                      </button>
                    </td>
                    <td className="num">{euros(d.amount_cents)}</td>
                    <td>{d.reason ?? '—'}</td>
                    <td>
                      <span
                        className={`pastilla ${pastillaDeDevolucion(d.status)}`}
                      >
                        {etiquetaDeEstadoDeDevolucion(d.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {lista.length === TOPE && (
            <p className="tenue">Se enseñan las {TOPE} más recientes.</p>
          )}
        </>
      )}
    </section>
  );
}
